import { useState, useCallback } from 'react';
import { Share2, Trash2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { useCalendarSharing } from '../hooks/useCalendarSharing';
import type { CalendarSharePermission, UserSearchResult } from '../types';

interface ShareCalendarDialogProps {
  open: boolean;
  onClose: () => void;
  calendarId: string | null;
  calendarName?: string;
}

export function ShareCalendarDialog({
  open,
  onClose,
  calendarId,
  calendarName,
}: ShareCalendarDialogProps) {
  const { shares, loading, createShare, updateShare, deleteShare } = useCalendarSharing({
    calendarId: open ? calendarId : null,
  });

  const [username, setUsername] = useState('');
  const [permission, setPermission] = useState<CalendarSharePermission>('read');
  const [showFreeBusyOnly, setShowFreeBusyOnly] = useState(false);
  const [searchResult, setSearchResult] = useState<UserSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!username.trim()) return;
    setSearching(true);
    try {
      const { users } = await api.searchCalendarUsers(username.trim());
      setSearchResult(users.length > 0 ? users[0] : null);
      if (users.length === 0) {
        toast({ title: 'User not found', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Search failed', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  }, [username]);

  const handleShare = useCallback(async () => {
    if (!searchResult) return;
    setCreating(true);
    try {
      await createShare({
        username: searchResult.username,
        permission,
        showFreeBusyOnly: permission === 'read' ? showFreeBusyOnly : false,
      });
      toast({ title: `Shared with @${searchResult.username}` });
      setUsername('');
      setSearchResult(null);
      setPermission('read');
      setShowFreeBusyOnly(false);
    } catch (err) {
      toast({
        title: 'Failed to share',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }, [searchResult, permission, showFreeBusyOnly, createShare]);

  const handleUpdatePermission = useCallback(
    async (shareId: string, newPermission: CalendarSharePermission) => {
      try {
        await updateShare(shareId, { permission: newPermission });
      } catch (err) {
        toast({
          title: 'Failed to update',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [updateShare],
  );

  const handleRevoke = useCallback(
    async (shareId: string, sharedUsername?: string) => {
      try {
        await deleteShare(shareId);
        toast({ title: `Revoked access for @${sharedUsername || 'user'}` });
      } catch (err) {
        toast({
          title: 'Failed to revoke',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
    },
    [deleteShare],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Share {calendarName || 'Calendar'}
          </DialogTitle>
        </DialogHeader>

        {/* Add share form */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="share-username" className="sr-only">
                Username
              </Label>
              <Input
                id="share-username"
                placeholder="Enter exact username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setSearchResult(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSearch}
              disabled={searching || !username.trim()}
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find'}
            </Button>
          </div>

          {searchResult && (
            <div className="rounded border p-3 space-y-3">
              <div className="text-sm">
                Found: <span className="font-medium">@{searchResult.username}</span>
                {searchResult.displayName !== searchResult.username && (
                  <span className="text-muted-foreground ml-1">({searchResult.displayName})</span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Select
                  value={permission}
                  onValueChange={(v) => setPermission(v as CalendarSharePermission)}
                >
                  <SelectTrigger className="w-28 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">Read</SelectItem>
                    <SelectItem value="write">Write</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>

                {permission === 'read' && (
                  <label className="flex items-center gap-1.5 text-xs">
                    <Checkbox
                      checked={showFreeBusyOnly}
                      onCheckedChange={(v) => setShowFreeBusyOnly(!!v)}
                      className="h-3.5 w-3.5"
                    />
                    Free/busy only
                  </label>
                )}
              </div>

              <Button size="sm" onClick={handleShare} disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Share
              </Button>
            </div>
          )}
        </div>

        {/* Existing shares */}
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : shares.length > 0 ? (
          <div className="space-y-2 mt-2">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Shared with
            </div>
            {shares.map((share) => (
              <div key={share.id} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate">
                  @{share.sharedWithUsername || share.sharedWithUserId}
                </span>
                <Select
                  value={share.permission}
                  onValueChange={(v) =>
                    handleUpdatePermission(share.id, v as CalendarSharePermission)
                  }
                >
                  <SelectTrigger className="w-24 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">Read</SelectItem>
                    <SelectItem value="write">Write</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  onClick={() => handleRevoke(share.id, share.sharedWithUsername)}
                  title="Revoke access"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-2">
            Not shared with anyone yet
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
